import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraCheckbox, SpecraSectionHeader, SpecraSegmentedControl, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { DETACHABLE_KINDS, defaultSurgeryMode, LINKABLE_KINDS, SURGERY_KIND_GROUPS } from './surgeryHooks';
export const selectionKey = (c)=>`${c.kind}:${c.id}`;
/** The picked plan components, with their chosen modes, for the RPC. */ export function selectedComponents(plan, selection) {
    return plan.filter((c)=>c.movable && selection[selectionKey(c)]).map((c)=>({
            ...c,
            mode: selection[selectionKey(c)]
        }));
}
function modeOptions(kind, allowDetach) {
    const out = [
        {
            value: 'move',
            label: 'Move'
        }
    ];
    if (LINKABLE_KINDS.has(kind)) out.push({
        value: 'link',
        label: 'Link'
    });
    if (allowDetach && DETACHABLE_KINDS.has(kind)) out.push({
        value: 'detach',
        label: 'Detach'
    });
    return out;
}
/**
 * GRW-19: the shared component picker of the Split and Move dialogs, grouped
 * by kind. M:N kinds (RFI responses, documents, recompete link) offer Move /
 * Link (RFI responses default to Link); the Move dialog adds Detach where
 * the server allows it. Blocked rows are disabled with the server's reason.
 */ export function SurgeryComponentPicker({ components, selection, onChange, allowDetach = false }) {
    const groups = SURGERY_KIND_GROUPS.map((g)=>({
            ...g,
            items: components.filter((c)=>c.kind === g.kind)
        })).filter((g)=>g.items.length > 0);
    if (groups.length === 0) {
        return /*#__PURE__*/ _jsx(SpecraText, {
            size: "meta",
            tone: "muted",
            children: "This pursuit has nothing that can move."
        });
    }
    const toggle = (c, checked)=>{
        const next = {
            ...selection
        };
        if (checked) next[selectionKey(c)] = defaultSurgeryMode(c.kind);
        else delete next[selectionKey(c)];
        onChange(next);
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-4",
        children: groups.map((g)=>/*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        title: g.label,
                        headingAs: "h4"
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: g.items.map((c)=>{
                            const key = selectionKey(c);
                            const mode = selection[key];
                            const options = modeOptions(c.kind, allowDetach);
                            const inputId = `surgery-${key}`;
                            return /*#__PURE__*/ _jsxs("li", {
                                className: "flex flex-wrap items-center gap-3",
                                "data-testid": Testids.pursuitSurgery.componentRow,
                                "data-kind": c.kind,
                                "data-component-id": c.id,
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                        id: inputId,
                                        checked: !!mode,
                                        disabled: !c.movable,
                                        onCheckedChange: (v)=>toggle(c, v === true),
                                        "aria-label": c.label
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraText, {
                                                size: "body-ui",
                                                children: c.label
                                            }),
                                            !c.movable && c.blockedReason ? /*#__PURE__*/ _jsx(SpecraText, {
                                                as: "div",
                                                size: "meta",
                                                tone: "muted",
                                                children: c.blockedReason
                                            }) : null
                                        ]
                                    }),
                                    mode && options.length > 1 ? /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                        size: "sm",
                                        label: `How to take ${c.label}`,
                                        testId: Testids.pursuitSurgery.modeControl,
                                        value: mode,
                                        options: options,
                                        onValueChange: (v)=>onChange({
                                                ...selection,
                                                [key]: v
                                            })
                                    }) : null
                                ]
                            }, key);
                        })
                    })
                ]
            }, g.kind))
    });
}
